/**
 * The single HTTP client for the whole web app.
 *
 * Every call goes through here so that credentials, the error envelope and the
 * 401 behaviour are handled once. Components never call fetch() directly.
 */

const BASE = '/api/v1';

/**
 * Mirrors the API's error envelope: { error: { code, message, details } }.
 * Components branch on `code` and display `message` verbatim - the API writes
 * its messages to be shown to a human.
 */
export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details ?? null;
  }

  /** True when the session has expired or was never established. */
  get isUnauthenticated() {
    return this.status === 401;
  }
}

async function request(path, { method = 'GET', body, signal } = {}) {
  let response;

  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      // Without this the session cookie is not sent and every call 401s.
      credentials: 'same-origin',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (error) {
    // AbortError is a deliberate cancellation, not a failure - let it through
    // untouched so callers can ignore it.
    if (error.name === 'AbortError') throw error;
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      'Could not reach the API. Check that the API process is running on port 5191.',
    );
  }

  // 204 and other empty responses have no body to parse.
  if (response.status === 204) return null;

  const text = await response.text();
  let payload = null;

  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // A non-JSON body from a 500 usually means a proxy or a crash page.
      if (!response.ok) {
        throw new ApiError(
          response.status,
          'INTERNAL_ERROR',
          `Server returned ${response.status} with an unreadable response.`,
        );
      }
      return null;
    }
  }

  if (!response.ok) {
    const envelope = payload?.error ?? {};
    throw new ApiError(
      response.status,
      envelope.code ?? 'INTERNAL_ERROR',
      envelope.message ?? `Request failed with status ${response.status}.`,
      envelope.details,
    );
  }

  return payload;
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
};

/* --- Endpoint helpers ------------------------------------------------------
   Named wrappers rather than raw paths scattered through components, so a
   route rename is a one-line change here. */

export const authApi = {
  me: () => api.get('/auth/me'),
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
};

export const healthApi = {
  check: () => api.get('/health'),
};

/**
 * API key management.
 *
 * Every one of these returns the same settings payload, so the page replaces
 * its state from whatever came back rather than patching it - there is no way
 * for the form and the server to drift apart.
 *
 * A key travels to the server and never comes back: responses carry a mask and
 * a verdict only.
 */
export const settingsApi = {
  apiKeys: () => api.get('/settings/api-keys'),

  /** Pass `apiKey`, `enabled`, or both. Omitted fields are left as they are. */
  saveCompaniesHouse: (patch) => api.put('/settings/api-keys/companies-house', patch),

  /**
   * With `apiKey`, trials that candidate without saving it. Without one, tests
   * the key already stored on the server.
   */
  testCompaniesHouse: (apiKey) =>
    api.post('/settings/api-keys/companies-house/test', apiKey ? { apiKey } : {}),

  removeCompaniesHouse: () => api.delete('/settings/api-keys/companies-house'),
};

/** Builds a query string, dropping empty values so `?section=` never appears. */
function queryString(params) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}

/**
 * The SIC catalogue and the four discovery routes.
 *
 * `search` and `browse` are different endpoints on purpose. `search` is the
 * ranked, four-tier, plain-English one used by the home screen; `browse` is the
 * literal filter used inside the classification tree, where a user looking at
 * division 78 expects a plain contains-match and would find tiered ranking
 * surprising.
 */
export const sicApi = {
  browse: ({ search, section, division, limit, offset } = {}) =>
    api.get(`/sic-codes${queryString({ search, section, division, limit, offset })}`),

  search: (q, { limit, signal } = {}) =>
    api.get(`/sic-codes/search${queryString({ q, limit })}`, { signal }),

  sections: () => api.get('/sic-codes/sections'),

  code: (code) => api.get(`/sic-codes/${code}`),

  categories: () => api.get('/sic-categories'),

  category: (slug) => api.get(`/sic-categories/${slug}`),

  /**
   * The one catalogue search box. `q` is whatever was typed; the server reads it
   * as a company number, name, director, location, or a mix. Costs Companies
   * House calls unless the same question was asked in the last 24 hours.
   * `readMore` reads more people and companies for a name search: more calls.
   */
  companySearch: (q, { includeInactive = false, readMore = false, signal } = {}) =>
    api.get(
      `/sic-codes/company-search${queryString({
        q,
        includeInactive: includeInactive ? 'true' : '',
        readMore: readMore ? 'true' : '',
      })}`,
      { signal },
    ),

  /**
   * The name-only search. Kept for existing callers; the catalogue box uses
   * `companySearch`, which runs the same engine.
   *
   * Costs one Companies House call unless the name was looked up in the last
   * 24 hours. Active companies only unless `includeInactive` is set - the
   * register holds decades of dissolved companies and an unfiltered name search
   * returns mostly those.
   */
  byCompany: (name, { includeInactive = false, signal } = {}) =>
    api.get(
      `/sic-codes/by-company${queryString({ name, includeInactive: includeInactive ? 'true' : '' })}`,
      { signal },
    ),

  /**
   * Re-reads `data/sic-aliases.json` and `data/sic-categories.json` into the
   * in-memory catalogue. Without this, an edit to either file needs a full API
   * restart to take effect - see Settings, where this is wired to a button.
   */
  reloadCatalogue: () => api.post('/sic-codes/reload'),
};

/**
 * The shortlist - the basket every discovery route feeds into.
 *
 * Server-side rather than localStorage: the brief requires it to survive a
 * refresh, and phase 4 acquires from it, which means the server has to be able
 * to read it without the browser handing it over.
 */
export const shortlistApi = {
  list: () => api.get('/shortlist'),

  /** Idempotent - adding a code already in the basket is a no-op, not an error. */
  add: (codes, { source = 'manual', sourceDetail } = {}) =>
    api.post('/shortlist', { codes: Array.isArray(codes) ? codes : [codes], source, sourceDetail }),

  remove: (code) => api.delete(`/shortlist/${code}`),

  clear: () => api.delete('/shortlist'),

  /** Spends one Companies House call per shortlisted code, so it is an explicit action. */
  preview: (companyStatus = ['active']) => api.post('/shortlist/preview', { companyStatus }),
};

/**
 * Acquisition - phase 4.
 *
 * Preview and create are separate calls on purpose. Preview costs one Companies
 * House call per code and commits to nothing; a run can take hours, so the
 * operator sees the real count and the real cost before anything starts.
 */
export const acquisitionApi = {
  /** Real `hits` per code plus the cost of each enrichment depth. Writes nothing. */
  preview: (body, { signal } = {}) => api.post('/acquisitions/preview', body, { signal }),

  /**
   * Returns 202 immediately with one queued run per code - never waits for the
   * work. Runs execute one at a time so they cannot compete for the rate limit.
   */
  create: (body) => api.post('/acquisitions', body),

  /** The log. Returns `total` alongside the page so it can say "20 of 400". */
  list: ({ limit = 20, offset = 0, status, sicCode, batchId, from, to } = {}) =>
    api.get(`/acquisitions${queryString({ limit, offset, status, sicCode, batchId, from, to })}`),

  /** Only the codes and statuses that appear in the log, for the filter controls. */
  filterOptions: () => api.get('/acquisitions/filter-options'),

  /** Polled every 2 seconds while a run is live. One indexed row read. */
  get: (uuid, { signal } = {}) => api.get(`/acquisitions/${uuid}`, { signal }),

  /**
   * The per-company outcome trail. `withCounts` runs a GROUP BY over every row
   * of the run, so the page asks for it when it opens and not on every page of
   * paging.
   *
   * `cursor` is the id of the last item on the previous page, keyset-paged -
   * see the route's own comment for why this replaced `offset`. Omit it for
   * the first page. `cursorValue` is the sorted column's own value on that
   * same row - required alongside `cursor` for any `sortBy` other than
   * `written`, since `written` alone keysets on `id`.
   */
  items: (
    uuid,
    { limit = 50, cursor, cursorValue, sortBy, sortDir, outcome, search, detail, withCounts } = {},
  ) =>
    api.get(
      `/acquisitions/${uuid}/items${queryString({
        limit,
        cursor,
        cursorValue,
        sortBy,
        sortDir,
        outcome,
        search,
        detail,
        withCounts: withCounts ? 'true' : '',
      })}`,
    ),

  /**
   * The download URL for the CSV, not the CSV itself.
   *
   * A plain same-origin link, so the browser streams the file straight to disk
   * with the session cookie attached. Fetching it through this client would
   * pull a 12 MB string into memory to hand straight back to a Blob, which is
   * the one thing the streamed endpoint exists to avoid. Always exported in
   * write order regardless of the on-screen sort - a full dump reads better
   * that way, and the export route never keysets on anything else.
   */
  exportUrl: (uuid, { outcome, search, detail } = {}) =>
    `${BASE}/acquisitions/${uuid}/export${queryString({ outcome, search, detail })}`,

  pause: (uuid) => api.post(`/acquisitions/${uuid}/pause`),
  resume: (uuid) => api.post(`/acquisitions/${uuid}/resume`),
  cancel: (uuid) => api.post(`/acquisitions/${uuid}/cancel`),

  /**
   * Cancel a scheduled run before it has started. A scheduled run has not yet
   * entered the queue, so this is equivalent to a pre-emptive cancel.
   */
  cancelScheduled: (uuid) => api.post(`/acquisitions/${uuid}/cancel`),
};

/**
 * The company registry - phase 6.
 *
 * Everything here except `lookup` is served from MySQL and costs no Companies
 * House calls, which is why the registry can be paged and filtered freely while
 * an acquisition is running.
 */
export const companiesApi = {
  /**
   * The master list. `limit` is capped at 200 by the API - each row carries its
   * SIC codes with descriptions, so a page is not free.
   */
  list: (
    {
      limit = 50,
      offset = 0,
      search,
      sicCode,
      status,
      entityType,
      hasWebsite,
      country,
      location,
      // Phase 8.
      hasDirector,
      directorSelectionAmbiguous,
      acquisitionId,
      sort,
      // Phase 7B financial filters.
      sizeBand,
      tradingVerdict,
      tradingAgeBand,
      netAssetsMin,
      netAssetsMax,
      turnoverMin,
      turnoverMax,
      profitable,
      hasCharges,
      hasInsolvency,
      filingsOverdue,
      trend,
      accountsStale,
    } = {},
    { signal } = {},
  ) =>
    api.get(
      `/companies${queryString({
        limit,
        offset,
        search,
        sicCode,
        status,
        entityType,
        hasWebsite,
        country,
        location,
        hasDirector,
        directorSelectionAmbiguous,
        acquisitionId,
        sort,
        sizeBand,
        tradingVerdict,
        tradingAgeBand,
        netAssetsMin,
        netAssetsMax,
        turnoverMin,
        turnoverMax,
        profitable,
        hasCharges,
        hasInsolvency,
        filingsOverdue,
        trend,
        accountsStale,
      })}`,
      { signal },
    ),

  /** The values the filter controls should offer, each with the count it would find. */
  filterOptions: () => api.get('/companies/filter-options'),

  /** One company in full, including every acquisition run that touched it. */
  get: (companyNumber, { signal } = {}) => api.get(`/companies/${companyNumber}`, { signal }),

  /**
   * Permanently removes this company and everything derived about it - phase
   * 11's data-protection delete action. The acquisition log keeps its record
   * that the company was found; only the live registry row and its SIC
   * codes, financials, directors and email guesses are removed.
   */
  deleteCompany: (companyNumber) => api.delete(`/companies/${companyNumber}`),

  /**
   * Permanently removes every company in the registry filed under one SIC
   * code, and everything derived about them - the catalogue/registry's
   * "delete category" bulk action. Same irreversibility as deleteCompany,
   * just scoped to a whole code instead of one row.
   */
  deleteBySicCode: (sicCode) => api.delete(`/companies/by-sic-code/${sicCode}`),

  /**
   * Fetches one company from Companies House now, outside any SIC run.
   *
   * The only call in this module that spends from the 600-per-5-minutes budget.
   * A company number is imported straight into the registry; a name comes back
   * as candidates to choose from, because importing twenty companies on the
   * strength of somebody typing "smith" would put rows in the registry that no
   * acquisition accounts for.
   */
  lookup: (query) => api.post('/companies/lookup', { query }),

  /**
   * Re-fetches one company's profile from Companies House now - phase 7.
   * Spends one API call, same as `lookup`. The company must already be in the
   * registry; this is a refresh, not a second way to import one.
   */
  refresh: (companyNumber) => api.post(`/companies/${companyNumber}/refresh`),

  /**
   * Fetches this company's filed accounts now - phase 7B. Several API calls
   * (filing history, the accounts document, charges, insolvency), not one -
   * see the rate limit on the route itself.
   */
  refreshFinancials: (companyNumber) => api.post(`/companies/${companyNumber}/refresh-financials`),

  /**
   * Re-resolves this company's directors now - phase 8: persons with
   * significant control first, the officers list as the fallback. One or two
   * API calls, so it carries the same rate limit as `refresh`.
   */
  refreshDirectors: (companyNumber) => api.post(`/companies/${companyNumber}/refresh-directors`),

  /**
   * Picks the primary director by hand when resolution came back ambiguous.
   * Spends no Companies House call.
   */
  selectDirector: (companyNumber, directorId) =>
    api.post(`/companies/${companyNumber}/select-director`, { directorId }),

  /**
   * Sets or clears a human classification override. `isAgency: null` clears
   * it and hands the verdict back to whatever the classifier decided.
   */
  setAgencyOverride: (companyNumber, isAgency) =>
    api.post(`/companies/${companyNumber}/agency-override`, { isAgency }),

  /**
   * Runs website discovery for this company now - phase 9: domain guessing
   * confirmed against the page, DuckDuckGo as the fallback. No Companies
   * House call, so no shared rate limit budget to worry about. Never
   * overwrites a website set by hand with `setWebsite` below.
   */
  discoverWebsite: (companyNumber) => api.post(`/companies/${companyNumber}/discover-website`),

  /**
   * Pastes in a website by hand. Confidence 100, and the discovery service
   * will never overwrite it afterwards - a second manual entry is a human
   * decision and simply replaces the first.
   */
  setWebsite: (companyNumber, website) => api.post(`/companies/${companyNumber}/set-website`, { website }),

  /**
   * Reads back this company's stored candidate email addresses - phase 10 -
   * without generating or probing anything.
   */
  getEmails: (companyNumber, { signal } = {}) => api.get(`/companies/${companyNumber}/emails`, { signal }),

  /**
   * Generates (or, with `refresh: true`, regenerates) ranked candidate
   * addresses for this company's directors, plus the standard role
   * mailboxes. No Companies House call and, by itself, no SMTP probe -
   * an MX lookup only, cached per domain.
   */
  generateEmails: (companyNumber, { refresh } = {}) =>
    api.post(`/companies/${companyNumber}/emails/generate`, refresh ? { refresh } : {}),

  /**
   * Generates this company's candidates, then soft-probes them. A no-op
   * beyond generation when SMTP_PROBE_ENABLED is off - every guess reads
   * "blocked" with a clear reason rather than a fake confidence score.
   */
  probeEmails: (companyNumber) => api.post(`/companies/${companyNumber}/emails/probe`),

  /**
   * Human override: marks one candidate as the address to use for outreach.
   * Also teaches the domain its naming convention, so every other director
   * there is re-priced accordingly.
   */
  chooseEmail: (companyNumber, guessId) => api.post(`/companies/${companyNumber}/emails/choose`, { guessId }),

  /**
   * Whether this host can open outbound SMTP connections at all - so the
   * Contact tab can tell the operator up front whether probing can work
   * from this machine, rather than after every guess comes back "blocked".
   */
  smtpEgress: ({ signal } = {}) => api.get('/smtp-egress', { signal }),

  /**
   * Re-runs agency classification over stored data - no Companies House calls.
   * Omit `sicCode` to reclassify the whole registry.
   */
  reclassify: (sicCode) => api.post('/companies/reclassify', sicCode ? { sicCode } : {}),

  /**
   * The download URL for the registry export, not the file itself - same
   * reason `acquisitionApi.exportUrl` is a URL: a same-origin link lets the
   * browser stream the file straight to disk rather than pulling a
   * potentially many-MB response into memory here to hand back as a Blob.
   * Honours every filter the list view accepts, plus `format` (csv|xlsx) and
   * a comma-joined `columns` - phase 11.
   */
  exportUrl: (filters = {}) => `${BASE}/companies/export${queryString(filters)}`,

  /** The export column catalogue - key and label - so the column picker never hard-codes its own copy. */
  exportColumns: () => api.get('/companies/export/columns'),
};

/**
 * Favorites - phase 13. A single flat starred basket, per user, the same
 * idempotent add/remove shape as the shortlist.
 */
export const favoritesApi = {
  list: () => api.get('/favorites'),

  add: (companyNumbers) =>
    api.post('/favorites', {
      companyNumbers: Array.isArray(companyNumbers) ? companyNumbers : [companyNumbers],
    }),

  remove: (companyNumber) => api.delete(`/favorites/${companyNumber}`),
};

/**
 * Named lists - phase 13. Unlike the shortlist and favorites there can be any
 * number of these, each named by hand and renamed at will, so the client
 * carries a create/rename/delete surface the single-basket APIs do not need.
 */
export const listsApi = {
  /** Every list, each with its member company numbers - enough to render membership ticks. */
  list: () => api.get('/lists'),

  /** One list in full, with every member company resolved for display. */
  get: (id, { signal } = {}) => api.get(`/lists/${id}`, { signal }),

  /** Optionally seeds the new list with companies in the same call. */
  create: (name, companyNumbers) =>
    api.post('/lists', companyNumbers?.length ? { name, companyNumbers } : { name }),

  rename: (id, name) => api.patch(`/lists/${id}`, { name }),

  remove: (id) => api.delete(`/lists/${id}`),

  addItems: (id, companyNumbers) =>
    api.post(`/lists/${id}/items`, {
      companyNumbers: Array.isArray(companyNumbers) ? companyNumbers : [companyNumbers],
    }),

  removeItem: (id, companyNumber) => api.delete(`/lists/${id}/items/${companyNumber}`),
};
